/**
 * The "Check Inventory" modal: batch-level stock of one product at the plants that can deliver it
 * to the customer, with a "not available locally, chat or call us" message when there is none.
 * Opened from the catalog (`components/Catalog/CatalogItems.tsx`), search
 * (`components/Search/Search.tsx`), product details (`components/Product/ProductDetails.tsx`), the
 * cart (`components/ShoppingCart/ShoppingCart.tsx`), quote details
 * (`components/QuoteDetails/QuoteDetails.tsx`) and shopping-list details
 * (`components/ShoppingListDetails/ShoppingListDetails.tsx`). Each caller owns the open flag and
 * passes `showBatchInformation` as "the line's delivery-times response listed at least one
 * available plant".
 *
 * Flow, each step triggered by an effect on the state the previous one set:
 * 1. Opening copies the caller's quantity and UOM into local state.
 * 2. The quantity / UOM change requests delivery times for this one product
 *    (`components/Helper/leadTimesHelpers.ts` → `getLeadTimeAndDeliveryDate`, Items
 *    `delivery-times`) and keeps its available plants, minus any plant that supplies the item
 *    through a vendor.
 * 3. With at least one plant left, `fetching` turns on and `getInventory` posts Items
 *    `items/batches` (`APIUrl.BATCH_INVENTORY`) for those plants; the batches fill the table.
 * 4. Closing reports the quantity and UOM back through `onUpdate`.
 *
 * Rules a change must keep:
 * - A vendor non-stock item (any lead-time day in the cart's delivery times,
 *   `cartDeliveryTimesData` from `@ryerson/frontend.e-commerce`, has a vendor number) never calls
 *   delivery-times or batches; it shows the "not available locally" message straight away. So does
 *   a product the caller opened with `showBatchInformation` false.
 * - The same message replaces the table when delivery times fail, no deliverable plant is left, or
 *   the batches call fails or returns an empty list.
 * - A quantity of 0 is sent as the quantity `getCorrectQuantityAndWeightOfProduct` returns for it,
 *   rounded down.
 *
 * Views: `./CheckInventoryModalDesktop.tsx` (a `Modal` with a `DataTable`) and
 * `./CheckInventoryModalMobile.tsx` (a slide-in panel); props and helpers in
 * `./CheckInventoryHelper.tsx`. Neither view renders the quantity / UOM input any more (it is
 * commented out in both), so in practice the quantity is the caller's. The input is dead code, to
 * be removed with the state and handlers that serve it (`batchDisplayQuantity` and its debounce
 * effect, `batchHandleOnChangeQty`, `batchUpdateQuantityUom`, `qtyUOMDropdown`, `isClosable`).
 *
 * Related: `unit-tests/checkInventoryModal.spec.tsx` (render smoke tests).
 */
import React, { useEffect, useState } from "react";
import { ContentSection } from "@ryerson/frontend.layout";
import CheckInventoryModalDesktop from "./CheckInventoryModalDesktop";
import CheckInventoryModalMobile from "./CheckInventoryModalMobile";
import { useApplication } from "@ryerson/frontend.application";
import { CheckInventoryContent } from "./CheckInventoryModalContent";
import {
	getCorrectQuantityAndWeightOfProduct,
	getCustomerPartNumber,
	updatedQuantityBasedOnQuantityUom,
	useDebounce,
} from "@ryerson/frontend.common";
import { APIUrl } from "@enums/apiUrl.enums";
import { isValidQuantity } from "@components/Helper/common";
import {
	Batch,
	CheckInventoryModalType,
	defaultBatchResponse,
	setUOMAndPriceUOMDropDown,
} from "./CheckInventoryHelper";
import { Option } from "@ryerson/frontend.form-controls";
import { lbs } from "@ryerson/common.uom";
import { isAxiosError } from "@components/Helper/TypeHelpers";
import { getAvailablePlant, getLeadTimeAndDeliveryDate } from "@components/Helper/leadTimesHelpers";
import { useEcommerce } from "@ryerson/frontend.e-commerce";
import { IDeliveryTimesResponse, ILeadTimeDay } from "@ryerson/common.models";
import { UOM } from "@ryerson/common.enums";
import { Media } from "@ryerson/frontend.next";

/**
 * Batch-level inventory modal for one product. See the file header for the request flow and the
 * cases that show the "not available locally" message.
 */
const CheckInventoryModal: React.FC<CheckInventoryModalType | any> = ({
	index,
	showCheckInventoryModal,
	setShowCheckInventoryModal,
	quantity,
	qtyUom,
	priceUom,
	product,
	onUpdate,
	showBatchInformation,
}) => {
	const {
		localization: { language },
		user: { isLoggedIn },
		axiosInstance,
		zipCode,
		profile,
	} = useApplication();

	const {
		customerPickup,
		selectedShipToAddress,
		salesPlant,
		defaultShipToAddress,
		cartDeliveryTimesData,
	} = useEcommerce();

	const content = CheckInventoryContent[language] || CheckInventoryContent.en;

	const defaultOption: Option = {
		display: lbs.display,
		value: lbs.value,
	};

	const [hasMounted, setHasMounted] = useState<boolean>(false);
	const [qtyUOMDropdown, setQtyUOMDropdown] = useState<Option[]>([defaultOption]);
	const [batches, setBatches] = useState<Batch[]>([defaultBatchResponse]);
	const [fetching, setFetching] = useState<boolean>(false);
	const [batchDisplayQuantity, setBatchDisplayQuantity] = useState<number>(quantity);
	const [batchQuantity, setBatchQuantity] = useState<number>(quantity);
	const [batchQuantityUom, setBatchQuantityUom] = useState<UOM | undefined>();
	const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
	const [deliverablePlant, setDeliverablePlant] = useState<number[]>([]);
	const [isClosable, setIsClosable] = React.useState<boolean>(false);
	const [isItemVendorNonStock, setIsItemVendorNonStock] = useState<boolean>(false); // an item is considered vendor non stock if any of the quantity is coming from a vendor

	let debouncedQuantity = batchDisplayQuantity?.toString();
	const debouncedQuantityTerm = useDebounce(debouncedQuantity, 500);

	useEffect(() => {
		if (!hasMounted) {
			setHasMounted(true);
		}
	}, []);

	useEffect(() => {
		if (hasMounted && showCheckInventoryModal) {
			setBatchDisplayQuantity(quantity);
			setBatchQuantity(quantity);
			setBatchQuantityUom(qtyUom);
		} else {
			setFetching(false);
			setShowErrorMessage(false);
			setBatches([defaultBatchResponse]);
			setBatchQuantityUom(undefined);
		}
	}, [hasMounted, showCheckInventoryModal]);

	useEffect(() => {
		if (showCheckInventoryModal) {
			isPlantAvailableForInventoryCheck();
		}
	}, [deliverablePlant]);

	useEffect(() => {
		if (
			showCheckInventoryModal &&
			debouncedQuantityTerm === batchDisplayQuantity?.toString() &&
			batchDisplayQuantity !== batchQuantity
		) {
			updatedQuantityBasedOnQtyUom(batchQuantityUom as UOM);
		}
	}, [debouncedQuantityTerm]);

	useEffect(() => {
		if (fetching && deliverablePlant.length) {
			getInventory();
		}
		if (!fetching) {
			setIsClosable(true);
		}
	}, [fetching]);

	useEffect(() => {
		if (product) {
			setUOMAndPriceUOMDropDown(product, profile, setQtyUOMDropdown);

			setIsItemVendorNonStock(
				!!cartDeliveryTimesData
					.find(
						(item) =>
							item.materialSku === (product?.materialMaster ?? product?.ryersonSku) // for some flows (e.g. header search results), ryersonSku is populated instead
					)
					?.leadTimeDays.some((leadTime) => leadTime.availabilityPlant?.vendorNo)
			);
		}
	}, [product]);

	useEffect(() => {
		if (showCheckInventoryModal) {
			// only call /delivery-times API if batch information needs to be shown and item does not have any quantity fulfilled by a vendor
			if (showBatchInformation && !isItemVendorNonStock) {
				if (product && batchQuantityUom) {
					setBatches([defaultBatchResponse]);
					let requestedQty = batchQuantity;
					if (batchQuantity === 0) {
						const { updatedQuantity } = getCorrectQuantityAndWeightOfProduct(
							product,
							batchQuantity,
							batchQuantityUom as UOM,
							priceUom as UOM
						);
						requestedQty = Math.floor(Number(updatedQuantity));
					}
					const requestBody = {
						qty: requestedQty,
						qtyUOM: batchQuantityUom,
						priceUOM: priceUom,
						comboKey: product.comboKey || "",
						id: 1,
						materialMaster: product.materialMaster
							? product.materialMaster
							: product.ryersonSku,
						customerPartNo: getCustomerPartNumber(product?.altPartNumber),
					};

					getLeadTimeAndDeliveryDate(
						isLoggedIn,
						axiosInstance,
						zipCode,
						customerPickup,
						selectedShipToAddress,
						defaultShipToAddress,
						salesPlant,
						[requestBody],
						(response) => {
							const responseData: IDeliveryTimesResponse[] = response?.data;
							if (responseData) {
								const leadTimeAndDeliveryDateData = responseData.find((leadTime) =>
									leadTime.materialSku === product.materialMaster
										? product.materialMaster
										: product.ryersonSku
								);

								// The find above parses as `(materialSku === materialMaster) ?
								// materialMaster : ryersonSku`, a SKU string rather than a
								// comparison, so it returns the first entry. Only this product is
								// requested, so that is its entry. Plants whose availability comes
								// from a vendor are left out of the batches request.
								const availablePlants = getAvailablePlant(
									(leadTimeAndDeliveryDateData || []) as IDeliveryTimesResponse
								).filter(
									(plant) =>
										!leadTimeAndDeliveryDateData?.leadTimeDays?.some(
											(item: ILeadTimeDay) =>
												item?.availabilityPlant?.plant === plant &&
												item?.availabilityPlant?.vendorNo
										)
								);

								setDeliverablePlant([...availablePlants]);
								isPlantAvailableForInventoryCheck();
							}
						},
						() => {
							setShowErrorMessage(true);
						}
					);
				}
			} else {
				setDeliverablePlant([]);
				isPlantAvailableForInventoryCheck();
			}
		}
	}, [batchQuantity, batchQuantityUom]);

	/**
	 * Starts the batches request (by turning `fetching` on) when there is a deliverable plant;
	 * otherwise shows the "not available locally" message. Reads `deliverablePlant` from the
	 * current render, so the call right after `setDeliverablePlant` sees the previous value; the
	 * effect on `deliverablePlant` calls it again with the new one.
	 */
	const isPlantAvailableForInventoryCheck = () => {
		if (showBatchInformation && deliverablePlant.length > 0) {
			setFetching(true);
			setShowErrorMessage(false);
		} else {
			setShowErrorMessage(true);
			setFetching(false);
		}
	};

	const batchUpdateQuantityUom = (value: UOM) => {
		updatedQuantityBasedOnQtyUom(value as UOM);
		setBatchQuantityUom(value);
	};

	const batchHandleOnChangeQty = (val: string): void => {
		let qty = Number(val);
		if (isValidQuantity(qty)) {
			setBatchDisplayQuantity(qty);
			setIsClosable(false);
		}
	};

	const updatedQuantityBasedOnQtyUom = (batchQuantityUom: UOM) => {
		if (product) {
			let updatedQuantity: number = updatedQuantityBasedOnQuantityUom(
				product,
				batchQuantityUom,
				batchDisplayQuantity
			);

			if (updatedQuantity !== batchQuantity) {
				setBatchQuantity(updatedQuantity);
			}

			if (updatedQuantity !== batchDisplayQuantity) {
				setBatchDisplayQuantity(updatedQuantity);
			}
		}
	};

	/**
	 * Posts Items `items/batches` for the product at the deliverable plants. A non-200 status or an
	 * empty list is treated as a failure and shows the "not available locally" message; `fetching`
	 * then stays on.
	 */
	const getInventory = async () => {
		let requestedQty = batchQuantity;
		if (batchQuantity === 0) {
			const { updatedQuantity } = getCorrectQuantityAndWeightOfProduct(
				product,
				batchQuantity,
				batchQuantityUom as UOM,
				priceUom as UOM
			);
			requestedQty = Math.floor(Number(updatedQuantity));
		}
		await axiosInstance
			.post(`${APIUrl.BATCH_INVENTORY}`, {
				skuNumber: product.materialMaster ? product.materialMaster : product.ryersonSku,
				comboKey: product.comboKey,
				quantity: batchQuantity > 0 ? batchQuantity : requestedQty,
				uom: batchQuantityUom,
				availabilityPlantNumbers: deliverablePlant,
				zipCode,
			})
			.then((response: any) => {
				let responseData = response?.data;
				if (isAxiosError(response) || response.status !== 200 || !responseData?.length) {
					throw response;
				}
				if (responseData) {
					let responseDataWithId = responseData.map((item: any, index: number) => {
						return {
							id: index + 1,
							...item,
						};
					});
					setBatches(responseDataWithId);
					setFetching(false);
				}
			})
			.catch((error) => {
				console.error(`Failed to get check inventory`, error);
				setShowErrorMessage(true);
			});
	};

	/**
	 * Closes the modal and reports the quantity and UOM back to the caller. `hasBeenChanged` is
	 * true when either differs from what the modal was opened with.
	 */
	const handleOnClose = () => {
		setFetching(false);
		setShowCheckInventoryModal(false);
		onUpdate({
			index: index,
			ryersonSku: product.materialMaster ? product.materialMaster : product.ryersonSku,
			quantity: batchQuantity,
			quantityUom: batchQuantityUom || lbs.value,
			hasBeenChanged: quantity !== batchQuantity || qtyUom !== batchQuantityUom,
		});
	};

	return (
		<ContentSection vPadding={"0"} hPadding={"0"}>
			<Media lessThan="lg">
				<CheckInventoryModalMobile
					showCheckInventoryModal={showCheckInventoryModal}
					setShowCheckInventoryModal={setShowCheckInventoryModal}
					content={content}
					batches={batches}
					qtyUomDropdown={qtyUOMDropdown}
					batchQtyUom={batchQuantityUom || lbs.value}
					batchPriceUom={priceUom}
					batchDisplayQuantity={batchDisplayQuantity}
					batchUpdateQuantityUom={batchUpdateQuantityUom}
					batchHandleOnChangeQty={batchHandleOnChangeQty}
					product={product}
					fetching={fetching}
					handleOnClose={handleOnClose}
					showItemNotAvailableLocallyMessage={showErrorMessage || isItemVendorNonStock}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<CheckInventoryModalDesktop
					showCheckInventoryModal={showCheckInventoryModal}
					setShowCheckInventoryModal={setShowCheckInventoryModal}
					content={content}
					batches={batches}
					qtyUomDropdown={qtyUOMDropdown}
					batchQtyUom={batchQuantityUom || lbs.value}
					batchPriceUom={priceUom}
					batchDisplayQuantity={batchDisplayQuantity}
					batchUpdateQuantityUom={batchUpdateQuantityUom}
					batchHandleOnChangeQty={batchHandleOnChangeQty}
					product={product}
					fetching={fetching}
					handleOnClose={handleOnClose}
					showItemNotAvailableLocallyMessage={showErrorMessage || isItemVendorNonStock}
					isClosable={isClosable}
				/>
			</Media>
		</ContentSection>
	);
};

export default CheckInventoryModal;
